import { Grid } from "@mui/material";
import { Alert } from "@mui/material";
import { useEffect, useState } from "react";
import { Control } from "react-hook-form";
import {
  NumberField,
  SelectFieldComponent,
  TextFieldComponent,
} from "../../external/ensolvers-core-frontend-mui/components/Common/FormComponents";
import GenericForm from "../../external/ensolvers-core-frontend-mui/components/Common/GenericForm";
import NotificationService from "../../external/ensolvers-core-frontend-mui/submodules/fox-typescript/services/NotificationService";
import DateUtils from "../../external/ensolvers-core-frontend-mui/submodules/fox-typescript/utils/DateUtils";
import Holiday from "../../models/Holiday";
import WorkLog, { WORKLOG_DESCRIPTION_MAX_LENGTH } from "../../models/WorkLog";
import HolidayUtils from "../../utils/HolidayUtils";
import NumberUtils from "../../utils/NumberUtils";
import OpsDateUtils from "../../utils/OpsDateUtils";
import RoleUtils from "../../utils/RoleUtils";
import { ButtonComponent } from "../ButtonComponent";
import WorkLogHoursWrapper from "../WorkLogHoursWrapper";
import TextScrollX from "../TextScrollX";
import "../../styles/ScrollBar.css";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import { ListComponentWithIcon } from "../ListComponentWithIcon";
import { EnsolversOpsUser } from "../../models/EnsolversOpsUser";
import Position from "../../models/Position";
import AuthService from "../../external/ensolvers-core-frontend-mui/submodules/fox-typescript/services/AuthService";
import { workLogCategory } from "../../utils/enums/WorkLogCategory";
import WorklogReportUtils from "../../utils/WorklogReportUtils";
import { useStyles } from "../Main/formStyles";
import { Token } from "../../theme";

interface Props {
  open: boolean;
  setOpen: (newValue: boolean) => void;
  isEditMode: boolean;
  setIsEditMode: (newValue: boolean) => void;
  workLog?: WorkLog;
  position?: Position;
  setWorkLog: (updatedWorklog: WorkLog | undefined) => void;
  holidays: Holiday[];
  paramUserExternalId?: string;
  user: EnsolversOpsUser;
  onSaveWorkLog: (workLog: WorkLog) => Promise<unknown>;
  onModalClose?: () => void;
}

export const WorklogModalCreation = ({
  open,
  setOpen,
  isEditMode,
  setIsEditMode,
  workLog,
  setWorkLog,
  holidays,
  paramUserExternalId,
  user,
  position,
  onSaveWorkLog,
  onModalClose,
}: Props) => {
  // Worklog detail fields
  const [prsPending, setPrsPending] = useState<string[]>([]);
  const [tasksDone, setTasksDone] = useState<string[]>([]);
  const [tasksInProgress, setTasksInProgress] = useState<string[]>([]);
  const [blockers, setBlockers] = useState<string[]>([]);
  const [reportedIssues, setReportedIssues] = useState<string[]>([]);
  const [resolvedIssues, setResolvedIssues] = useState<string[]>([]);

  const customStyles = useStyles();

  useEffect(() => {
    if (workLog) {
      const worklogDetails = workLog.details;
      if (worklogDetails) {
        !!worklogDetails.prsPending && setPrsPending(worklogDetails.prsPending || []);
        !!worklogDetails.tasksDone && setTasksDone(worklogDetails.tasksDone || []);
        !!worklogDetails.tasksInProgress && setTasksInProgress(worklogDetails.tasksInProgress || []);
        !!worklogDetails.blockers && setBlockers(worklogDetails.blockers || []);
        !!worklogDetails.reportedIssues && setReportedIssues(worklogDetails.reportedIssues || []);
        !!worklogDetails.resolvedIssues && setResolvedIssues(worklogDetails.resolvedIssues || []);
      }
    }
  }, [workLog]);

  const canModifiedWorklog = (): boolean => {
    return (
      RoleUtils.isAdminOrManager() ||
      paramUserExternalId === undefined ||
      paramUserExternalId === user.externalId ||
      paramUserExternalId === AuthService.getUser()?.externalId! ||
      position !== null
    );
  };

  const handleCopyExternalDescription = (workLog: WorkLog, control: Control) => {
    setWorkLog({
      ...workLog,
      hours: control._formValues.hours,
      internalHours: control._formValues.internalHours,
      description: control._formValues.description,
      internalDescription: control._formValues.description,
    });
  };

  const resetWorklog = () => {
    setIsEditMode(false);

    setPrsPending([]);
    setTasksDone([]);
    setTasksInProgress([]);
    setBlockers([]);
    setReportedIssues([]);
    setResolvedIssues([]);
  };

  const hideWorklogModal = () => {
    setWorkLog(undefined);
    resetWorklog();
    onModalClose?.();
    setOpen(false);
  };

  const handleOnSave = async (workLogCreated: WorkLog, submitContext: any) => {
    if (!workLog) {
      return;
    }

    if (NumberUtils.hasDecimals(workLogCreated?.hours) || NumberUtils.hasDecimals(workLogCreated?.internalHours)) {
      NotificationService.error("Hours cannot have decimals");
      setWorkLog(workLogCreated);
      return;
    }

    if (workLogCreated.adjustmentHours != null && !WorklogReportUtils.adjustmentHoursIsValid(workLogCreated, position)) {
      setWorkLog(workLogCreated);
      return;
    }

    if (workLogCreated.bonusHours != null && !WorklogReportUtils.bonusHoursIsValid(workLogCreated, position)) {
      setWorkLog(workLogCreated);
      return;
    }

    workLogCreated.details = {
      prsPending,
      tasksDone,
      tasksInProgress,
      blockers,
      reportedIssues,
      resolvedIssues,
    };

    if (!isEditMode) {
      workLogCreated.shortDay = OpsDateUtils.toUTCWithSetHours(workLogCreated.shortDay);
    }

    try {
      await onSaveWorkLog(workLogCreated);
    } catch {
      return;
    }

    hideWorklogModal();

    // Pressing Enter keeps the modal open on the next working day of the same month.
    if (!isEditMode && submitContext?.trigger === "ENTER_KEY_PRESSED") {
      const lastDate = workLog.shortDay.getDate();
      workLog.shortDay.setDate(workLog.shortDay.getDate() + 1);

      while (OpsDateUtils.isWeekend(workLog.shortDay) || HolidayUtils.isHoliday(workLog.shortDay, holidays)) {
        workLog.shortDay.setDate(workLog.shortDay.getDate() + 1);
      }

      if (lastDate < workLog.shortDay.getDate()) {
        setWorkLog(workLog);
        setOpen(true);
      }
    }
  };

  return (
    <GenericForm<WorkLog>
      headerStyles={{ color: Token.textPrimary, marginBottom: -10, marginTop: 10 }}
      buttonStyles={{ marginRight: 20, color: "white", backgroundColor: Token.actionPrimary, marginBottom: 10 }}
      additionalButtonStyles={{
        color: Token.textSecondary,
        border: `1px solid ${Token.borderDefault}`,
        marginBottom: 10,
      }}
      dialogStyles={customStyles.root}
      dialog={true}
      open={open}
      instance={workLog}
      title={(isEditMode ? "Edit " : "Create ") + DateUtils.formatAsDDDMMMYYY(workLog?.shortDay!) + " Worklog "}
      onSave={handleOnSave}
      onCancel={resetWorklog}
      validateBeforeClosing
      disableBackdropClick
      renderContent={(register, errors, control) => (
        <div className="scrollableY">
          {(workLog?.isWeekend || workLog?.isHoliday) && (
            <Alert severity="warning" sx={{ ml: "0.5em", mb: "1em" }}>
              The day you selected is a <b>{workLog?.isWeekend ? "WEEKEND" : "HOLIDAY"}</b> day. If you report hours here, they will be
              considered extra hours.
            </Alert>
          )}

          <TextFieldComponent propertyName={"assignationName"} label="Assignation" required={true} readOnly />

          {/* External hours */}
          <WorkLogHoursWrapper
            HoursElement={
              <NumberField
                propertyName={"hours"}
                label="Hours"
                required={true}
                inputType="number"
                readOnly={!canModifiedWorklog()}
                enableEditInputProps={true}
                min={0}
                max={24}
              />
            }
            DescriptionElement={
              <TextFieldComponent
                propertyName={"description"}
                label="Description"
                required={true}
                internalProps={{ autoFocus: true, inputProps: { maxLength: WORKLOG_DESCRIPTION_MAX_LENGTH } }}
                readOnly={!canModifiedWorklog()}
              />
            }
          />

          {/* Internal hours */}
          <WorkLogHoursWrapper
            HoursElement={
              <NumberField
                propertyName={"internalHours"}
                label="Internal Hours"
                required={false}
                inputType="number"
                readOnly={!canModifiedWorklog()}
                enableEditInputProps={true}
                min={0}
                max={24}
              />
            }
            DescriptionElement={
              <TextFieldComponent
                propertyName={"internalDescription"}
                label="Internal Description"
                required={false}
                internalProps={{ inputProps: { maxLength: WORKLOG_DESCRIPTION_MAX_LENGTH } }}
                readOnly={!canModifiedWorklog()}
              />
            }
          />

          <ButtonComponent onClick={() => handleCopyExternalDescription(workLog!, control)} text={"Copy external description"} />

          {RoleUtils.isAdminOrManager() && (
            <Grid container spacing={1}>
              <Grid item xs={3}>
                <NumberField
                  propertyName={"adjustmentHours"}
                  label="Adjustment"
                  required={false}
                  inputType="number"
                  pattern={/^-?[0-9]+$/}
                  customHelperText="Adjustment should be an integer."
                  enableEditInputProps={true}
                  max={24}
                />
              </Grid>
              {!!position && (position.bonus == 0 || position.bonus == null) && (
                <Grid item xs={3}>
                  <NumberField
                    propertyName={"bonusHours"}
                    label="Bonus"
                    required={false}
                    inputType="number"
                    enableEditInputProps={true}
                    min={0}
                    pattern={/^[0-9]+$/}
                    customHelperText="Bonus should be a number greater than or equal to zero."
                  />
                </Grid>
              )}
            </Grid>
          )}

          <SelectFieldComponent
            control={control}
            register={register}
            defaultValue={"REGULAR"}
            errors={errors}
            propertyName={"category"}
            label={"Worklog Type"}
            required={true}
            options={Object.entries(workLogCategory).map(([value, label]) => ({
              label,
              value,
            }))}
          />
          {/* Worklog details fields */}
          <ListComponentWithIcon
            title={"PRS Pending"}
            name="prs-pending"
            items={prsPending}
            renderItem={(item, index) => <TextScrollX>• {item}</TextScrollX>}
            onAdd={(newItem) => setPrsPending([...prsPending, newItem])}
            onDelete={(itemIndex) => setPrsPending(prsPending.filter((item, index) => index !== itemIndex))}
            icon={<ContentCopyIcon fontSize="small" />}
            handleOnClick={() => {
              navigator.clipboard.writeText(prsPending.toString());
            }}
          />
          <ListComponentWithIcon
            title={"Tasks Done"}
            name="tasks-done"
            items={tasksDone}
            renderItem={(item, index) => <TextScrollX>• {item}</TextScrollX>}
            onAdd={(newItem) => setTasksDone([...tasksDone, newItem])}
            onDelete={(itemIndex) => setTasksDone(tasksDone.filter((item, index) => index !== itemIndex))}
            icon={<ContentCopyIcon fontSize="small" />}
            handleOnClick={() => {
              navigator.clipboard.writeText(tasksDone.toString());
            }}
          />
          <ListComponentWithIcon
            title={"Tasks In Progress"}
            name="tasks-in-progress"
            items={tasksInProgress}
            renderItem={(item, index) => <TextScrollX>• {item}</TextScrollX>}
            onAdd={(newItem) => setTasksInProgress([...tasksInProgress, newItem])}
            onDelete={(itemIndex) => setTasksInProgress(tasksInProgress.filter((item, index) => index !== itemIndex))}
            icon={<ContentCopyIcon fontSize="small" />}
            handleOnClick={() => {
              navigator.clipboard.writeText(tasksInProgress.toString());
            }}
          />
          <ListComponentWithIcon
            title={"Blockers"}
            name="blockers"
            items={blockers}
            renderItem={(item, index) => <TextScrollX>• {item}</TextScrollX>}
            onAdd={(newItem) => setBlockers([...blockers, newItem])}
            onDelete={(itemIndex) => setBlockers(blockers.filter((item, index) => index !== itemIndex))}
            icon={<ContentCopyIcon fontSize="small" />}
            handleOnClick={() => {
              navigator.clipboard.writeText(blockers.toString());
            }}
          />
          <ListComponentWithIcon
            title={"Reported issues"}
            name="reported-issues"
            items={reportedIssues || []}
            renderItem={(item, index) => <TextScrollX>• {item}</TextScrollX>}
            onAdd={(newItem) => setReportedIssues([...reportedIssues, newItem])}
            onDelete={(itemIndex) => setReportedIssues(reportedIssues.filter((item, index) => index !== itemIndex))}
            icon={<ContentCopyIcon fontSize="small" />}
            handleOnClick={() => {
              navigator.clipboard.writeText(reportedIssues.toString());
            }}
          />
          <ListComponentWithIcon
            title={"Resolved issues"}
            name="resolved-issues"
            items={resolvedIssues || []}
            renderItem={(item, index) => <TextScrollX>• {item}</TextScrollX>}
            onAdd={(newItem) => setResolvedIssues([...resolvedIssues, newItem])}
            onDelete={(itemIndex) => setResolvedIssues(resolvedIssues.filter((item, index) => index !== itemIndex))}
            icon={<ContentCopyIcon fontSize="small" />}
            handleOnClick={() => {
              navigator.clipboard.writeText(resolvedIssues.toString());
            }}
          />
        </div>
      )}
      hideCancel
      additionalButtons={[
        {
          label: "Cancel",
          onAction: hideWorklogModal,
        },
      ]}
    />
  );
};
